<template>
    <div v-show="alwaysShow || total !== '0.00'" id="braintree-form" :class="variant">
        <section v-if="paymentMethods.length > 0">
            <h3 :is="`h${titleHeadingLevel}`">{{ title }}</h3>
            <div id="userCreditCards">
                <p v-if="description">{{ description }}</p>
                <table class="table borderless selectCard">
                    <thead>
                    <tr>
                        <th></th>
                        <th>Payment Method</th>
                        <th class="hidden-xs">Expires</th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(option, index) in paymentMethods" :key="option.token" style="height: 40px;">
                        <td class="option">
                            <label class="radio-label">
                                <input
                                    type="radio"
                                    :id="`card${index}`"
                                    :disabled="disabled"
                                    class="cardRadio"
                                    :value="option.token"
                                    v-model="token" />
                                &nbsp;
                                <i class="radio"></i>
                            </label>
                        </td>
                        <td class="cc-image-td" v-if="option.paymentType === 'card'">
                            <img role="card-type-image" :src="option.image" :alt="`${option.type} Card`" />
                            <label :for="`card${index}`">Card ending in <span role="card-last-4-digits">{{ option.last4 }}</span></label>
                        </td>
                        <td class="cc-image-td" v-else>
                            <img role="card-type-image" :src="option.image" :alt="`${option.type} Account`" />
                            <label :for="`card${index}`">Account ending in <span role="card-last-4-digits">{{ option.last4 }}</span></label>
                        </td>
                        <td class="hidden-xs" v-if="option.paymentType === 'card'">{{ option.cc_month }}/{{ option.cc_year }}</td>
                        <td class="hidden-xs" v-else>&nbsp;</td>
                        <td class="center cc-actions">
                            <div class="btn tiny">
                                <button @click.prevent="deletePaymentMethod(option, index)" :disabled="disabled" class="show-delete-cc-modal">
                                    Delete
                                </button>
                            </div>
                        </td>
                    </tr>
                    <tr>
                        <td class="option">
                            <label class="radio-label">
                                <input
                                    type="radio"
                                    id="newCC"
                                    class="cardRadio"
                                    value="token"
                                    :disabled="disabled"
                                    v-model="token" />
                                &nbsp;
                                <i class="radio"></i>
                            </label>
                        </td>
                        <td v-if="achEligible"><label for="newCC">New Card or ACH Direct Debit</label></td>
                        <td v-else><label for="newCC">New Card</label></td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </section>
        <section v-show="token === 'token' && !braintreeLoading && !disabled && achEligible">
            <h3 :is="`h${newCardHeadingLevel}`" v-if="newCardHeading">{{ newCardHeading }}</h3>
            <div class="form-group">
                <label for="cardNumber" class="col-sm-2 col-xs-12 control-label">
                    Payment Type:
                </label>
                <div class="btn-group col-md-8 col-sm-8 col-xs-12">
                    <button type="button"
                            class="btn btn-primary"
                            :class="{ 'active': paymentType === 'card' }"
                            @click="switchPaymentType('card')">Credit Card</button>
                    <button type="button"
                            class="btn btn-primary"
                            :class="{ 'active': paymentType === 'ach' }"
                            @click="switchPaymentType('ach')">ACH Direct Debit</button>
                </div>
            </div>
        </section>
        <div v-show="token === 'token' && !braintreeLoading && !disabled && paymentType === 'card'">
            <div class="form-group">
                <label for="cardNumber" class="col-sm-2 col-xs-12 control-label" :class="{ 'is-invalid': errors.includes('cardNumber') }" v-if="variant !== 'dark'">
                    Card Number: <span class="required">*</span>
                </label>
                <div id="cardNumberContainer" class="col-md-8 col-sm-8 col-xs-12">
                    <div id="cardNumber" class="form-control braintree" :class="{ 'is-invalid': errors.includes('cardNumber') }"></div>
                </div>
            </div>
            <div id="expirationCvvContainer" class="form-group">
                <label for="expirationMonth" class="col-sm-2 col-xs-12 control-label" :class="{ 'is-invalid': errors.includes('expirationMonth') || errors.includes('expirationYear') }" v-if="variant !== 'dark'">
                    Expiration Date: <span class="required">*</span>
                </label>
                <div id="expirationMonthContainer" class="col-md-2 col-sm-3 col-xs-6">
                    <div id="expirationMonth" class="form-control braintree" :class="{ 'is-invalid': errors.includes('expirationMonth') }"></div>
                </div>
                <div id="expirationYearContainer" class="col-md-2 col-sm-3 col-xs-6">
                    <div id="expirationYear" class="form-control braintree" :class="{ 'is-invalid': errors.includes('expirationYear') }"></div>
                </div>

                <label id="cvvLabel" for="cvv" class="col-sm-1 col-xs-12 control-label" :class="{ 'is-invalid': errors.includes('cvv') }" v-if="variant !== 'dark'">
                    CVV: <span class="required">*</span>
                </label>
                <div id="cvvContainer" class="col-md-2 col-sm-3 col-xs-12">
                    <div id="cvv" class="form-control braintree" :class="{ 'is-invalid': errors.includes('cvv') }"></div>
                </div>
            </div>
            <div class="form-group">
                <label for="postalCode" class="col-sm-2 col-xs-12 control-label" :class="{ 'is-invalid': errors.includes('postalCode') }" v-if="variant !== 'dark'">
                    Postal Code: <span class="required">*</span>
                </label>
                <div id="postalCodeContainer" class="col-md-3 col-sm-3 col-xs-12">
                    <div id="postalCode" class="form-control braintree" :class="{ 'is-invalid': errors.includes('postalCode') }"></div>
                </div>

                <label for="countryCode" class="col-sm-2 col-xs-12 control-label" :class="{ 'is-invalid': errors.includes('countryCode') }" v-if="variant !== 'dark'">
                    Country: <span class="required">*</span>
                </label>
                <div id="countryCodeContainer" class="col-md-3 col-sm-3 col-xs-12">
                    <select id="countryCode" class="form-control" v-model="countryCode" :class="{ 'is-invalid': errors.includes('countryCode') }">
                        <option :value="country.code" :key="country.code" v-for="country in countries">{{ country.name }}</option>
                    </select>
                </div>
            </div>
            <div class="form-group" v-if="this.user && this.variant !== 'dark' && !forceSave">
                <label class="col-sm-2 col-xs-12 control-label" :class="{ 'is-invalid': errors.includes('cc_remember') }">
                    Save Card:
                </label>
                <div class="col-md-4 col-sm-5 col-xs-12">
                    <label for="cc_remember" class="checkbox-label" :class="{ 'is-invalid': errors.includes('cc_remember') }">
                        <input type="checkbox" id="cc_remember" v-model="ccRemember">
                        &nbsp;
                        <i class="checkmark"></i>
                    </label>
                </div>
            </div>
            <div class="form-group" v-else-if="this.user && !forceSave">
                <div id="saveCardContainer" class="col-md-4 col-sm-5 col-xs-12">
                    <label for="cc_remember" class="checkbox-label" :class="{ 'is-invalid': errors.includes('cc_remember') }">
                        <input type="checkbox" id="cc_remember" v-model="ccRemember">
                        &nbsp;
                        <i class="checkmark"></i>
                    </label>
                    <label id="saveCardLabel" class="col-sm-2 col-xs-12 control-label" :class="{ 'is-invalid': errors.includes('cc_remember') }">
                        Save Card
                    </label>
                </div>
            </div>
        </div>
        <div v-show="token === 'token' && !braintreeLoading && !disabled && paymentType === 'ach'">
            <div class="form-group">
                <label for="routingNumber" class="col-sm-2 col-xs-12 control-label" :class="{ 'is-invalid': errors.includes('routingNumber') }" v-if="variant !== 'dark'">
                    Routing Number: <span class="required">*</span>
                </label>
                <div id="routingNumberContainer" class="col-md-8 col-sm-8 col-xs-12">
                    <input v-model="routingNumber" class="form-control" :class="{ 'is-invalid': errors.includes('routingNumber') }" :placeholder=" variant === 'dark' ? 'Routing Number*' :'' ">
                </div>
            </div>
            <div class="form-group">
                <label for="accountNumber" class="col-sm-2 col-xs-12 control-label" :class="{ 'is-invalid': errors.includes('accountNumber') }" v-if="variant !== 'dark'">
                    Account Number: <span class="required">*</span>
                </label>
                <div id="accountNumberContainer" class="col-md-8 col-sm-8 col-xs-12">
                    <input v-model="accountNumber" class="form-control" :class="{ 'is-invalid': errors.includes('accountNumber') }":placeholder=" variant === 'dark' ? 'Account Number*' :'' ">
                </div>
            </div>
            <div class="form-group">
                <label class="col-sm-2 col-xs-12 control-label" :class="{ 'is-invalid': errors.includes('accountType') }">
                    Account Type: <span class="required" style="display: inline">*</span>
                </label>
                <div class="col-md-8 col-sm-8 col-xs-12">
                    <label class="radio-inline pl-0">
                        <input type="radio" v-model="accountType" name="accountType" value="checking"> Checking
                    </label>
                    <label class="radio-inline pl-0">
                        <input type="radio" v-model="accountType" name="accountType" value="savings"> Savings
                    </label>
                </div>
            </div>
            <div class="form-group">
                <label class="col-sm-2 col-xs-12 control-label" :class="{ 'is-invalid': errors.includes('ownershipType') }">
                    Ownership Type: <span class="required" style="display: inline">*</span>
                </label>
                <div class="col-md-8 col-sm-8 col-xs-12">
                    <label class="radio-inline pl-0">
                        <input type="radio" v-model="ownershipType" name="ownershipType" value="personal"> Personal
                    </label>
                    <label class="radio-inline pl-0">
                        <input type="radio" v-model="ownershipType" name="ownershipType" value="business"> Business
                    </label>
                </div>
            </div>
            <div class="form-group" :class="{ 'd-flex': variant === 'dark' }" v-if="ownershipType === 'personal'">
                <label for="firstName" class="col-sm-2 col-xs-12 control-label" :class="{ 'is-invalid': errors.includes('firstName') }" v-if="variant !== 'dark'">
                    First Name: <span class="required">*</span>
                </label>
                <div id="firstNameContainer" class="col-md-3 col-sm-3 col-xs-12">
                    <input v-model="firstName" class="form-control" :class="{ 'is-invalid': errors.includes('firstName') }" :placeholder=" variant === 'dark' ? 'First Name*' :'' ">
                </div>
                <label for="lastName" class="col-sm-2 col-xs-12 control-label" :class="{ 'is-invalid': errors.includes('lastName') }" v-if="variant !== 'dark'">
                    Last Name: <span class="required">*</span>
                </label>
                <div id="lastNameContainer" class="col-md-3 col-sm-3 col-xs-12">
                    <input v-model="lastName" class="form-control" :class="{ 'is-invalid': errors.includes('lastName') }" :placeholder=" variant === 'dark' ? 'Last Name*' :'' ">
                </div>
            </div>
            <div class="form-group" v-if="ownershipType === 'business'">
                <label for="businessName" class="col-sm-2 col-xs-12 control-label" :class="{ 'is-invalid': errors.includes('businessName') }" v-if="variant !== 'dark'">
                    Business Name: <span class="required">*</span>
                </label>
                <div id="businessNameContainer" class="col-md-8 col-sm-8 col-xs-12">
                    <input v-model="businessName" class="form-control" :class="{ 'is-invalid': errors.includes('businessName') }" :placeholder=" variant === 'dark' ? 'Business Name*' :'' ">
                </div>
            </div>
            <div class="form-group" v-if="ownershipType !== ''">
                <label for="streetAddress" class="col-sm-2 col-xs-12 control-label" :class="{ 'is-invalid': errors.includes('streetAddress') }" v-if="variant !== 'dark'">
                    Street Address: <span class="required">*</span>
                </label>
                <div id="streetAddressContainer" class="col-md-8 col-sm-8 col-xs-12">
                    <input v-model="streetAddress" class="form-control" :class="{ 'is-invalid': errors.includes('streetAddress') }" :placeholder=" variant === 'dark' ? 'Street Address*' :'' ">
                </div>
            </div>
            <div class="form-group" :class="{ 'd-flex': variant === 'dark' }" v-if="ownershipType !== ''">
                <label for="locality" class="col-sm-2 col-xs-12 control-label" :class="{ 'is-invalid': errors.includes('locality') }" v-if="variant !== 'dark'">
                    City: <span class="required">*</span>
                </label>
                <div id="localityContainer" class="col-md-3 col-sm-3 col-xs-12">
                    <input v-model="locality" class="form-control" :class="{ 'is-invalid': errors.includes('locality') }" :placeholder=" variant === 'dark' ? 'City*' :'' ">
                </div>
                <label for="region" class="col-sm-2 col-xs-12 control-label" :class="{ 'is-invalid': errors.includes('region') }" v-if="variant !== 'dark'">
                    State: <span class="required">*</span>
                </label>
                <div id="regionContainer" class="col-md-3 col-sm-3 col-xs-12">
                    <input v-model="region" class="form-control" :class="{ 'is-invalid': errors.includes('region') }" :placeholder=" variant === 'dark' ? 'State*' :'' " v-if="usStates === null">
                    <select v-model="region" class="form-control" :class="{ 'is-invalid': errors.includes('region') }" v-else>
                        <option value="" v-if="variant === 'dark'" disabled>
                            State*
                        </option>
                        <option v-for="(state, code) in usStates" :value="code">
                            {{ state }}
                        </option>
                    </select>
                </div>
            </div>
            <div class="form-group" v-if="ownershipType !== ''">
                <label for="postalCode" class="col-sm-2 col-xs-12 control-label" :class="{ 'is-invalid': errors.includes('postalCode') }" v-if="variant !== 'dark'">
                    Postal Code: <span class="required">*</span>
                </label>
                <div id="achPostalCodeContainer" class="col-md-3 col-sm-3 col-xs-12">
                    <input v-model="postalCode" class="form-control" :class="{ 'is-invalid': errors.includes('postalCode') }" :placeholder=" variant === 'dark' ? 'Postal Code*' :'' ">
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import api from '../../api';
    import bus from './../../bus';
    import client from "braintree-web/dist/browser/client";
    import usBankAccount from "braintree-web/dist/browser/us-bank-account";
    import hostedFields from "braintree-web/dist/browser/hosted-fields";
    import dataCollector from "braintree-web/dist/browser/data-collector";
    import threeDSecure from "braintree-web/dist/browser/three-d-secure";
    import countries from './Countries';

    export default {
        props: {
            alwaysShow: {
                type: Boolean,
                default: false,
            },
            clientToken: {
                type: String,
                required: true,
            },
            currency: {
                type: String,
                default: 'usd',
            },
            description: {
                type: String,
                default: '',
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            newCardHeading: {
                type: String,
                default: 'PAYMENT',
            },
            newCardHeadingLevel: {
                type: Number,
                default: 3,
            },
            title: {
                type: String,
                default: 'PAYMENT',
            },
            titleHeadingLevel: {
                type: Number,
                default: 3,
            },
            total: {
                type: String,
                required: true,
            },
            user: {
                type: Object,
                required: false,
                default: null,
            },
            variant: {
                type: String,
                default: '',
            },
            verifyCard: {
                type: Boolean,
                default: true,
            },
            ctaText: {
                type: String,
                default: 'Complete Checkout',
            },
            usStates: {
                type: Object,
                required: false,
                default: null,
            },
            achEnabled: {
                type: Boolean,
                default: false,
            },
            activeToken: {
                type: String,
                required: false,
                default: '',
            },
            forceSave: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                bin: null,
                braintreeLoading: false,
                braintreeLoaded: false,
                paymentMethods: [],
                allPaymentMethods: [],
                paymentType: 'card',
                ccRemember: true,
                countries: countries,
                countryCode: this.getDefaultCountry(this.user ? this.user.country : null),
                darkStyles: {
                    'input, select': {
                        'font-family': '\'trade_gothic_regular\', Arial, Helvetica, sans-serif',
                        'font-size': '20px',
                        'color': 'white',
                        'padding': '10px 15px',
                        'border': '2px solid transparent',
                        'height': '50px',
                    },
                    'input::placeholder, select::placeholder': {
                        'color': 'white',
                    },
                    'select': {
                        'appearance': 'none',
                        '-webkit-appearance': 'none',
                        '-moz-appearance': 'none',
                        '-ms-appearance': 'none',
                        '-o-appearance': 'none',
                    },
                    'option': {
                        'color': 'black',
                    },
                },
                dataCollectorInstance: false,
                defaultStyles: {
                    'input, select': {
                        'font-size': '14px',
                        'color': '#555',
                    },
                    'select': {
                        '-webkit-appearance': 'none',
                    },
                    'input.invalid': {
                        'color': '#e86a6b',
                    },
                },
                errors: [],
                fields: [
                    'cardNumber',
                    'expirationMonth',
                    'expirationYear',
                    'cvv',
                    'postalCode',
                    'countryCode',
                ],
                nonce: '',
                hostedFieldInstance: false,
                threeDSecureInstance: false,
                threeDSecureEnrolled: false,
                usBankAccountInstance: false,
                token: 'token',

                routingNumber: '',
                accountNumber: '',
                accountType: 'checking',
                ownershipType: '',
                streetAddress: '',
                extendedAddress: '',
                locality: '',
                region: '',
                postalCode: '',
                firstName: '',
                lastName: '',
                businessName: '',
            }
        },
        mounted() {
            const promises = [];
            if (this.user) {
                promises.push(this.getCards());
            }
            if (this.alwaysShow || this.total !== '0.00') {
                promises.push(this.createBraintreeClient());
            }
            bus.$on('currencyChanged', this.currencyChanged);
        },
        methods: {
            currencyChanged(currency) {
                this.switchPaymentType(currency != 'usd' ? 'card' : this.paymentType)
                this.reloadBraintreeClient()
            },
            switchPaymentType(paymentType) {
                this.paymentType = paymentType;
                this.paymentMethods = this.allPaymentMethods
                if (!this.achEligible) {
                    this.paymentMethods = this.allPaymentMethods.filter(card => card.paymentType === 'card')
                }
                this.$emit('paymentTypeChanged', paymentType)
                bus.$emit('paymentTypeChanged', paymentType)
            },
            async getBillingValues() {
                if (this.total === '0.00') {
                    return {
                        cc_number: '',
                        cc_month: '',
                        cc_year: '',
                        cc_cvv: '',
                        billing_country: '',
                        billing_postal_code: '',
                        token: this.token,
                        cc_remember: '',
                        paymentType: this.paymentType,
                    };
                } else if (this.nonce === '') {
                    return false;
                }

                if (this.paymentType === 'ach') {
                    return {
                        nonce: this.nonce,
                        token: this.token,
                        device: this.dataCollectorInstance.deviceData,
                        paymentType: this.paymentType,
                    };
                }

                this.canceled = false;
                if (this.verifyCard && this.threeDSecureInstance) {
                    const onCustomerCanceled = () => {
                        this.canceled = true;
                    };

                    this.threeDSecureInstance.on('customer-canceled', onCustomerCanceled);
                    const response = await this.threeDSecureInstance.verifyCard({
                        amount: this.total,
                        nonce: this.nonce,
                        bin: this.bin,
                        onLookupComplete: function (data, next) {
                            next();
                        },
                    });
                    this.nonce = response.nonce;
                    this.threeDSecureEnrolled = ["Y", 0].includes(response.threeDSecureInfo.enrolled);
                    this.threeDSecureInstance.off('customer-canceled', onCustomerCanceled);
                }

                if (this.canceled) {
                    return false;
                } else {
                    return {
                        billing_country: this.countryCode,
                        cc_remember: !this.useSavedCC ? this.ccRemember : '',
                        nonce: this.nonce,
                        token: this.token,
                        device: this.dataCollectorInstance.deviceData,
                        threeDSecureEnabled: this.threeDSecureEnrolled,
                        paymentType: this.paymentType,
                    };
                }
            },
            async getCards() {
                try {
                    const response = await api.getUserPaymentMethods();
                    if (response.data.length > 0) {
                        this.allPaymentMethods = response.data;
                        this.paymentMethods = this.allPaymentMethods
                        if (!this.achEligible) {
                            this.paymentMethods = this.allPaymentMethods.filter(option => option.paymentType === 'card')
                        }
                        let defaultOption = this.paymentMethods.find(option => option.token === this.activeToken)
                            ?? this.paymentMethods.find((option) => option.default );
                        if (defaultOption) {
                            this.paymentType = defaultOption.paymentType;
                            this.token = defaultOption.token;
                        }
                    }
                } catch(error) {}
            },
            async deletePaymentMethod(paymentMethod, index) {
                try {
                    this.braintreeLoading = true;
                    if (paymentMethod.paymentType === 'card') {
                        await api.deleteUserCreditCard(paymentMethod.token);
                    } else {
                        await api.deleteUserDebitAccount(paymentMethod.token);
                    }
                    this.paymentMethods.splice(index, 1);
                    this.$noty.success('Payment Method Deleted Successfully');

                    if (this.token === paymentMethod.token || this.paymentMethods.length === 0) {
                        this.token = 'token';
                    }

                } catch(error) {
                    let message = error?.response?.data?.message ?? 'There was a problem deleting the payment method.';
                    this.$noty.error(message);
                } finally {
                    this.braintreeLoading = false;
                }
            },
            recreateBraintreeClient() {
                this.destroyBraintreeClient();
                return this.createBraintreeClient();
            },
            createBraintreeClient() {
                this.braintreeLoading = true;
                return client.create({
                    authorization: this.clientToken,
                }).then((clientInstance) => {
                    return Promise.all([
                        hostedFields.create({
                            client: clientInstance,
                            styles: this.styles,
                            fields: {
                                number: {
                                    selector: '#cardNumber',
                                    placeholder: this.variant === 'dark' ? 'Card Number*' : '',
                                },
                                cvv: {
                                    selector: '#cvv',
                                    placeholder: this.variant === 'dark' ? 'Security Code*' : '',
                                },
                                expirationMonth: {
                                    selector: '#expirationMonth',
                                    placeholder: this.variant === 'dark' ? 'Month*' : 'Month',
                                    select: {
                                        options: [
                                            'January',
                                            'February',
                                            'March',
                                            'April',
                                            'May',
                                            'June',
                                            'July',
                                            'August',
                                            'September',
                                            'October',
                                            'November',
                                            'December',
                                        ],
                                    },
                                },
                                expirationYear: {
                                    selector: '#expirationYear',
                                    placeholder: this.variant === 'dark' ? 'Year*' : 'Year',
                                    select: true,
                                },
                                postalCode: {
                                    selector: "#postalCode",
                                    placeholder: this.variant === 'dark' ? 'Billing Postal Code*' : '',
                                },
                            }
                        }),
                        this.verifyCard ? threeDSecure.create({
                            version: 2,
                            client: clientInstance,
                        }) : Promise.resolve(false),
                        dataCollector.create({
                            client: clientInstance,
                        }),
                        this.achEligible ? usBankAccount.create({
                            client: clientInstance,
                        }) : Promise.resolve(false),
                    ]);
                }).then(([hostedFieldInstance, threeDSecureInstance, dataCollectorInstance, usBankAccountInstance]) => {
                    hostedFieldInstance.on('validityChange', function (event) {
                        let field = event.fields[event.emittedBy];

                        if (field.isValid) {
                            $(field.container).removeClass('is-invalid');
                            $(field.container.parentNode.parentNode.firstChild).removeClass('is-invalid');
                        }
                    });

                    this.hostedFieldInstance = hostedFieldInstance;
                    this.threeDSecureInstance = threeDSecureInstance;
                    this.dataCollectorInstance = dataCollectorInstance;
                    this.usBankAccountInstance = usBankAccountInstance;
                    this.braintreeLoading = false;
                    this.braintreeLoaded = true;
                    this.$emit('loading', this.braintreeLoading);
                }).catch((err) => {
                    console.log(err)
                    this.$noty.error('There was a problem with your card. Please try again.');
                    this.braintreeLoading = false;
                    this.hostedFieldInstance = err.code;
                });
            },
            destroyBraintreeClient() {
                this.braintreeLoading = true;
                if (this.hostedFieldInstance) {
                    this.hostedFieldInstance.teardown();
                    this.hostedFieldInstance = null;
                }
                if (this.threeDSecureInstance) {
                    this.threeDSecureInstance.teardown();
                    this.threeDSecureInstance = null;
                }
                if (this.dataCollectorInstance) {
                    this.dataCollectorInstance.teardown();
                    this.dataCollectorInstance = null;
                }
                if (this.usBankAccountInstance) {
                    this.usBankAccountInstance.teardown();
                    this.usBankAccountInstance = null;
                }
                this.braintreeLoading = false;
                this.braintreeLoaded = false;
            },
            getCurrencyCountryCode() {
                switch (this.currency) {
                    case 'cad':
                        return 'CA'; // Canada
                    case 'gbp':
                    case 'eur':
                        return 'GB'; // United Kingdom
                }
                return 'US';
            },
            getDefaultCountry(code) {
                const defaultCountryCode = this.getCurrencyCountryCode();
                if (! code) {
                    return defaultCountryCode;
                } else {
                    const userCountry = code.trim().toUpperCase();
                    const country = countries.find((c) => c.code === userCountry);
                    return country ? country.code : defaultCountryCode;
                }
            },
            processClientError(code) {
                switch (code) {
                    case 'THREEDS_CARDINAL_SDK_ERROR':
                        this.errors = ['cardNumber'];
                        this.$noty.error('An unexpected error occurred processing your payment. Please try a different card.');
                        return true;
                }
                return false;
            },
            processServerError(field) {
                switch (field) {
                    case 'billing_country':
                        field = 'countryCode';
                        break;
                    case 'billing_postal_code':
                        field = 'postalCode';
                        break;
                    case 'cc_number':
                        field = 'cardNumber';
                        break;
                    case 'cc_month':
                        field = 'monthYear';
                        break;
                    case 'cc_year':
                        field = 'expirationYear';
                        break;
                    case 'cc_cvv':
                        field = 'cvv';
                        break;
                    case 'convenience_fee':
                        this.errors.push('convenience_fee');
                        return true;
                }
                if (this.fields.includes(field)) {
                    if (!this.errors.includes(field)) {
                        this.errors.push(field);
                    }
                    return true;
                }
                return false;
            },
            reloadBraintreeClient() {
                if (! this.braintreeLoading) {
                    if (this.clientToken) {
                        if (this.braintreeLoaded) {
                            this.recreateBraintreeClient();
                        } else {
                            this.createBraintreeClient();
                        }
                    } else if (this.braintreeLoaded) {
                        this.destroyBraintreeClient();
                    }
                }
            },
            async validateBilling() {
                this.nonce = '';
                this.bin = null;
                this.errors = [];

                if (this.useSavedCC) {
                    const response = await api.getSavedCCNonce(this.token);
                    this.nonce = response.data.nonce;
                    return true;
                }

                if (this.paymentType === 'ach') {
                    return await this.validateAch();
                }

                return this.validateCard();
            },
            async validateAch() {
                let errorMessages = [];
                if (this.routingNumber.length < 8 || this.routingNumber.length > 9) {
                    this.errors.push('routingNumber')
                    errorMessages.push('Routing number must contain 8 or 9 digits')
                }
                if (this.accountNumber.length < 1 || this.accountNumber.length > 17) {
                    this.errors.push('accountNumber')
                    errorMessages.push('Account number must contain 1-17 alphanumeric characters')
                }
                if (!this.accountType) {
                    this.errors.push('accountType')
                    errorMessages.push('Account type required')
                }
                if (!this.ownershipType) {
                    this.errors.push('ownershipType')
                    errorMessages.push('Ownership type required')
                }
                if (this.ownershipType === 'personal') {
                    if (!this.firstName) {
                        this.errors.push('firstName')
                        errorMessages.push('First name required')
                    }
                    if (!this.lastName) {
                        this.errors.push('lastName')
                        errorMessages.push('Last name required')
                    }
                }
                if (this.ownershipType === 'business') {
                    if (!this.businessName) {
                        this.errors.push('businessName')
                        errorMessages.push('Business name required')
                    }
                }
                if (!this.streetAddress && this.ownershipType) {
                    this.errors.push('streetAddress')
                    errorMessages.push('Street address required')
                }
                if (!this.locality && this.ownershipType) {
                    this.errors.push('locality')
                    errorMessages.push('City required')
                }
                if (!this.region && this.ownershipType) {
                    this.errors.push('region')
                    errorMessages.push('State required')
                }
                if (!this.postalCode && this.ownershipType) {
                    this.errors.push('postalCode')
                    errorMessages.push('Postal code required')
                }
                if (errorMessages.length) {
                    return errorMessages;
                }

                try {
                    var bankDetails = {
                        accountNumber: this.accountNumber,
                        routingNumber: this.routingNumber,
                        accountType: this.accountType,
                        ownershipType: this.ownershipType,
                        billingAddress: {
                            streetAddress: this.streetAddress,
                            locality: this.locality,
                            region: this.region,
                            postalCode: this.postalCode,
                        }
                    };
                    if (bankDetails.ownershipType === 'personal') {
                        bankDetails.firstName = this.firstName;
                        bankDetails.lastName = this.lastName;
                    } else {
                        bankDetails.businessName = this.businessName;
                    }

                    const response = await this.usBankAccountInstance.tokenize({
                        bankDetails: bankDetails,
                        mandateText: this.mandateText,
                    });
                    this.nonce = response.nonce;
                    return true;
                } catch (e) {
                    switch (e.code) {
                        case 'US_BANK_ACCOUNT_TOKENIZATION_NETWORK_ERROR':
                            const message = e.details.originalError.details.originalError[0].message;
                            let error = message.substring(message.search(': ') + 2)
                            const replacementMap = {
                                'UsBankAccountNumber': 'Account number',
                                'account_number': 'account number',
                                'UsBankRoutingNumber': 'Routing number',
                                'routing_number': 'routing number',
                                'UsZipCode': 'Postal Code',
                            }
                            for (var prop in replacementMap) {
                                if (error.search(prop) > -1) {
                                    let prefix = '';
                                    if (error.search('Values of type') > -1) {
                                        prefix = 'Values of type ';
                                    }
                                    error = error.replace(prefix + prop, replacementMap[prop])
                                }
                            }
                            errorMessages.push(error);
                            break;
                        default:
                            return ['An unexpected error occurred processing your account. Please try again later.'];
                    }

                    return errorMessages;
                }
            },
            async validateCard() {
                try {
                    const payload = await this.hostedFieldInstance.tokenize();
                    this.nonce = payload.nonce;
                    this.bin = payload.details.bin;
                } catch (e) {
                    switch (e.code) {
                        case 'HOSTED_FIELDS_FIELDS_EMPTY':
                            this.errors = this.fields;
                            break;
                        case 'HOSTED_FIELDS_FIELDS_INVALID':
                            if (e.details && Array.isArray(e.details.invalidFieldKeys)) {
                                this.errors = [];
                                e.details.invalidFieldKeys.forEach((key) => {
                                    this.errors.push(key === 'number' ? 'cardNumber' : key);
                                });
                            }
                            break;
                        case 'HOSTED_FIELDS_TOKENIZATION_FAIL_ON_DUPLICATE':
                            // only happens if error on duplication is included in the request.
                            this.errors = ['cc_remember'];
                            return ['You have already saved that card.'];
                        case 'HOSTED_FIELDS_TOKENIZATION_CVV_VERIFICATION_FAILED':
                            // customer id and verifyCard must be set in the request
                            this.errors = ['cvv'];
                            break;
                        case 'HOSTED_FIELDS_FAILED_TOKENIZATION':
                            this.errors = this.fields;
                            return ['There was a problem with your card information.'];
                        case 'HOSTED_FIELDS_TOKENIZATION_NETWORK_ERROR':
                            return ['Unable to verify your card. Please try again later.'];
                        default:
                            console.error(e);
                            return ['An unexpected error occurred processing your card. Please try again later.'];
                    }

                    let errorMessages = [];
                    this.errors.forEach((field) => {
                        switch (field) {
                            case 'cardNumber':
                                errorMessages.push('Card number is invalid');
                                break;
                            case 'expirationMonth':
                                if (!this.errors.includes('expirationYear')) {
                                    errorMessages.push('Card is expired or date is invalid');
                                }
                                break;
                            case 'expirationYear':
                                errorMessages.push('Card is expired or date is invalid');
                                break;
                            case 'cvv':
                                errorMessages.push('Card CVV is invalid');
                                break;
                            case 'postalCode':
                                errorMessages.push('Postal code is invalid');
                                break;
                            case 'countryCode':
                                errorMessages.push('Country code is invalid');
                                break;
                        }
                    });
                    return errorMessages;
                }
                return true;
            }
        },
        computed: {
            styles() {
                return this.variant ? this.darkStyles : this.defaultStyles;
            },
            useSavedCC() {
                return this.token !== 'token';
            },
            achEligible() {
                return this.currency === 'usd'
                    && this.achEnabled;

            },
            mandateText() {
                return 'By clicking "' + this.ctaText +
                    '", I authorize Braintree, a service of PayPal, on behalf of Bisnow to verify my bank account ' +
                    'information using bank information and consumer reports and I authorize Bisnow to initiate an ' +
                    'ACH/electronic debit to my checking/savings account. I agree the ACH transactions I authorize ' +
                    'comply with all applicable laws. Purchases via ACH may appear on your account as "PAYPAL".'
            }
        },
        watch: {
            braintreeLoading(val) {
                this.$emit('loading', val);
            },
            clientToken(val) {
                this.reloadBraintreeClient();
            },
            hostedFieldInstance(val) {
                this.$emit('braintreeFields', val);
            },
            total(newTotal, oldTotal) {
                if (
                    ! this.alwaysShow &&
                    ((newTotal !== '0.00' && oldTotal === '0.00') || (newTotal === '0.00' && oldTotal !== '0.00'))
                ) {
                    this.reloadBraintreeClient();
                }
            },
            token(val) {
                const option = this.paymentMethods.find((method) => method.token === val );
                if (option) {
                    this.switchPaymentType(option.paymentType)
                }
            }
        },
    }
</script>

<style lang="scss" scoped>
    #braintree-form {
        h3 {
            margin-bottom: 0;
            font-size: 22px;
            margin-top: 40px;

            @media (max-width: 500px) {
                font-size: 18px;
                margin: 0;
                padding: 35px 0 0;
            }
        }
        p {
            @media (max-width: 500px) {
                font-size: 14px;
            }
        }
        input, select, .braintree.form-control {
            background-color: #fbfbfb;
            border: 1px solid #dddddd;
            height: 40px;
            border-radius: 5px;
            box-shadow: none;

            &.is-invalid {
                border: 1px solid #e86a6b;
            }
        }

        select {
            background-image: url('https://s3.amazonaws.com/cdn.bisnow.net/assets/website/selectArrowBlack.png');
            background-size: 8px;
        }

        label {
            padding-top: 15px;
            padding-left: 0;
            font-size: 13px;
            white-space: nowrap;

            &.is-invalid {
                color: #e86a6b;
            }

            @media (max-width: 768px) {
                padding-left: 15px;
                margin-bottom: 0;
                padding-bottom: 0;
            }
        }

        .radio-inline {
            padding-left: 20px;

            input {
                height: 13px;
                margin-top: 1px;
            }
        }

        .required {
            color: #e86a6b;
            font-size: 14px;
        }

        .form-group {
            margin-bottom: 0;
        }
    }

    input[type=checkbox]#cc_remember, input[type=radio].cardRadio {
        height: 20px !important;
    }

    table.selectCard {
        max-width: 500px;
        margin-bottom: 10px;

        td, th {
            text-align: left;
        }

        td {

            label {
                padding: 0 !important;
                font-weight: normal;
                margin-bottom: 0;

                &:hover {
                    cursor: pointer;
                }
            }

            &.option {
                padding: 0 10px;

                &:hover {
                    cursor: pointer;
                }
                @media (max-width: 500px) {
                    padding: 0 5px 0 0;
                }
            }

            @media (max-width: 500px) {
                font-size: 13px;
            }
        }
    }
    .cc-image-td img {
        width: auto;
        max-height: 20px;
        vertical-align: middle;
        margin-right: 10px;
    }

    #braintree-form.dark {
        input,
        select,
        .form-control {
            background-color: #666;
            border: 2px solid #666;
            border-radius: 0;
        }
        .control-label {
            width: 100% !important;
        }
        .control-label .required {
            display: none;
        }
    }

    #cardNumber,
    #expDate,
    #expirationMonth,
    #expirationYear,
    #cvv,
    #postalCode {
        background-color: #fbfbfb !important;
        border: 1px solid #dddddd;
        height: 40px;
        border-radius: 5px;
        box-shadow: none;
        color: #555;

        &.braintree-hosted-fields-invalid, &.is-invalid {
            border-color: #e86a6b;
        }
    }

    #expirationMonth,
    #expirationYear {
        background-image: url(https://s3.amazonaws.com/cdn.bisnow.net/assets/website/selectArrowBlack.png);
        background-size: 8px;
        background-repeat: no-repeat;
        background-position: 95% center;
        -moz-appearance: none;
        -webkit-appearance: none !important;
        -ms-appearance: none;
        -o-appearance: none;
        appearance: none;
    }
    .dark #cardNumber,
    .dark #expDate,
    .dark #expirationMonth,
    .dark #expirationYear,
    .dark #cvv,
    .dark #postalCode {
        background-color: #666 !important;
        border: 2px solid #666;
        border-radius: 0;
    }
    .dark select option {
        background-color: #666 !important;
        color: white !important;
    }

    .dark {
        #cardNumberContainer,
        #routingNumberContainer,
        #accountNumberContainer,
        #streetAddressContainer,
        #businessNameContainer {
            width: 100%;
        }

        #firstNameContainer,
        #lastNameContainer,
        #localityContainer,
        #regionContainer,
        #achPostalCodeContainer {
            width: 50%;
        }

        #routingNumberContainer .form-control,
        #accountNumberContainer .form-control,
        #streetAddressContainer .form-control,
        #firstNameContainer .form-control,
        #lastNameContainer .form-control,
        #localityContainer .form-control,
        #regionContainer .form-control,
        #achPostalCodeContainer .form-control,
        #businessNameContainer .form-control {
            font-family: trade_gothic_regular, Arial, Helvetica, sans-serif;
            font-size: 20px;
            color: white;
            padding: 20px 15px;
            width: 100%;
            &::placeholder {
                color: white;
            }
        }

        .btn-group {
            position: relative;
            display: inline-block;
            vertical-align: middle;
        }
        .btn-group>.btn:first-child:not(:last-child):not(.dropdown-toggle) {
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
        }
        .btn-group>.btn:first-child {
            margin-left: 0;
        }
        .btn-group>.btn, .btn-group-vertical>.btn {
            position: relative;
            float: left;
        }
        .btn-primary {
            color: #ffffff;
            background-color: #999999;
        }
        .btn {
            display: inline-block;
            padding: 6px 12px;
            margin-bottom: 0;
            font-size: 14px;
            font-weight: 400;
            line-height: 1.42857143;
            text-align: center;
            white-space: nowrap;
            vertical-align: middle;
            cursor: pointer;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
            user-select: none;
            background-image: none;
            border: 0;
            border-radius: 4px;
        }
        .btn-group>.btn:last-child:not(:first-child), .btn-group>.dropdown-toggle:not(:first-child) {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
        }
        .btn-group .btn+.btn, .btn-group .btn+.btn-group, .btn-group .btn-group+.btn, .btn-group .btn-group+.btn-group {
            margin-left: -1px;
        }
        .btn-primary:active, .btn-primary.active, .open>.dropdown-toggle.btn-primary {
            background-image: none;
        }
        .btn:active, .btn.active {
            background-image: none;
            outline: 0;
            -webkit-box-shadow: inset 0 3px 5px rgba(0, 0, 0, .125);
            box-shadow: inset 0 3px 5px rgba(0, 0, 0, .125);
        }
        .btn-primary:hover, .btn-primary:focus, .open>.dropdown-toggle.btn-primary {
            color: #ffffff;
            background-color: #999999;
            border-color: #285e8e;
            box-shadow: inset 0 3px 5px rgba(0, 0, 0, .25);
        }

        .btn-primary:active, .btn-primary.active {
            color: #ffffff;
            background-color: #F58320;
        }

        #regionContainer select.form-control {
            background-image: url('https://cdn.bisnow.net/fit?height=7&type=png&url=https%3A%2F%2Fs3.amazonaws.com%2Fcdn.bisnow.net%2Fassets%2Fwebsite%2FselectArrowWhite.png&width=14&sign=eGNbpo1MPxQoQZw65aapbdXnc--rwwzSTCj6ti4uYHw');
            line-height: normal;
            height: 44px;
            padding: 0 0 0 15px;
        }
    }

    .d-flex {
        display: flex;
    }

    .dark #expirationCvvContainer {
        display: flex;
        box-sizing: border-box;
        clear: both;
    }
    .dark #expirationMonthContainer,
    .dark #expirationYearContainer,
    .dark #cvvContainer {
        width: 33.33333333%;
        box-sizing: border-box;
    }
    .dark #postalCodeContainer, .dark #countryCodeContainer {
        width: calc(50% - 4px);
    }
    .dark #countryCodeContainer select {
        line-height: 40px;
        height: inherit;
    }
    .dark #saveCardContainer {
        display: flex;
    }
    .dark #saveCardLabel {
        padding: 8px;
    }

    .dark #expirationMonth,
    .dark #expirationYear,
    .dark #countryCodeContainer select{
        background-image: url(https://cdn.bisnow.net/fit?height=7&type=png&url=https%3A%2F%2Fs3.amazonaws.com%2Fcdn.bisnow.net%2Fassets%2Fwebsite%2FselectArrowWhite.png&width=14&sign=eGNbpo1MPxQoQZw65aapbdXnc--rwwzSTCj6ti4uYHw);
    }
</style>>

<style type="text/css">
    #Cardinal-ModalContent, #Cardinal-CCA-IFrame {
        min-height: 75vh;
    }
</style>
